import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/shared/hooks/useAppMutation'
import { updateGiftCardProfile } from '../api'
import { syncUpdatedProfile } from './useUpdateProfile'
import { useAuthStore } from '@/shared/stores/auth'
import { useCountryStore } from '@/shared/stores/country'
import { detectCountry } from '@/shared/constants/countries'

export interface UseSetPreferredCountryReturn {
  mutate: (countryCode: string | null) => Promise<void>
  isPending: boolean
  error: Error | null
}

function applyCountryChange(
  countryCode: string | null,
  user: ReturnType<typeof useAuthStore.getState>['user'],
  setUser: ReturnType<typeof useAuthStore.getState>['setUser'],
  setCountry: ReturnType<typeof useCountryStore.getState>['setCountry']
) {
  const resolvedCountry = countryCode ?? detectCountry()

  if (user) {
    setUser({ ...user, prefCountry: countryCode })
  }

  if (countryCode) {
    localStorage.setItem('preferredCountry', countryCode)
  } else {
    localStorage.removeItem('preferredCountry')
  }

  setCountry(resolvedCountry, countryCode !== null)
}

export function useSetPreferredCountry(): UseSetPreferredCountryReturn {
  const queryClient = useQueryClient()
  const setUser = useAuthStore((state) => state.setUser)
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const setCountry = useCountryStore((state) => state.setCountry)

  const {
    mutate: attemptProfileUpdate,
    isPending,
    error,
  } = useAppMutation({
    mutationFn: (countryCode: string | null) => updateGiftCardProfile({ prefCountry: countryCode }),
    onSuccess: (updatedUser) => {
      syncUpdatedProfile(updatedUser)
    },
    mapResult: (data) => data !== null,
  })

  return {
    mutate: async (countryCode: string | null) => {
      applyCountryChange(countryCode, user, setUser, setCountry)

      if (!isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ['giftcards', 'catalog'] })
        queryClient.invalidateQueries({ queryKey: ['giftcards', 'offers'] })
        return
      }

      const succeeded = await attemptProfileUpdate(countryCode)
      if (succeeded) {
        queryClient.invalidateQueries({ queryKey: ['giftcards', 'catalog'] })
        queryClient.invalidateQueries({ queryKey: ['giftcards', 'offers'] })
      }
    },
    isPending,
    error,
  }
}
